import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
} from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { ContactOption } from '@/components/onboarding/ContactOption.tsx';
import { fetchColleagues, type ColleagueListItem } from '@/lib/colleagues.ts';
import {
  addFollowException,
  type MeetingFollowExceptionItem,
} from '@/lib/meetingFollow.ts';
import { followCorrectionFor } from '@/lib/personCorrections.ts';
import { m } from '@/paraglide/messages.js';

/** From here on the list is scanned rather than read, so it gets a search box. */
const SEARCHABLE_FROM = 8;

/** Somebody there is something to follow for: a colleague with an address. */
type AddressedColleague = ColleagueListItem & { email: string };

export interface AddColleagueDialogProps {
  /** Every correction the user has, so the list can leave out who is followed. */
  corrections: readonly MeetingFollowExceptionItem[];
}

/**
 * Adds one colleague whose one-to-ones Wundo follows, the same pick onboarding
 * offers.
 *
 * The person is chosen from the user's own calendar rather than typed: a name
 * beside a face is what somebody recognises, and the address comes along with
 * it instead of being spelled out.
 */
export function AddColleagueDialog({ corrections }: AddColleagueDialogProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  /* What was just picked, so the row answers before the dialog closes. */
  const [picked, setPicked] = useState<string | null>(null);

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const { data: colleagues } = useQuery({
    queryKey: ['colleagues', user?.id, 'active'],
    enabled: Boolean(user?.id) && open,
    queryFn: () => fetchColleagues(user!.id, { hidden: false }),
  });

  const add = useMutation({
    mutationFn: addFollowException,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['meetingFollowExceptions'],
      });
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
      close();
    },
  });

  const close = () => {
    setOpen(false);
    setSearch('');
    setPicked(null);
    add.reset();
  };

  const pickable = addable(colleagues, corrections);
  const shown = matching(pickable, search);

  const pick = (colleague: AddressedColleague) => {
    setPicked(colleague.id);
    add.mutate({
      target_kind: 'person',
      email: colleague.email,
      follow: true,
      record: null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <DialogTrigger render={<Button size='sm' variant='quiet' />}>
        {m.meetings_colleague_add()}
      </DialogTrigger>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>{m.meetings_colleague_add()}</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-3 px-5 py-4'>
          {pickable.length > SEARCHABLE_FROM && (
            <Input
              aria-label={m.meetings_colleague_search()}
              placeholder={m.meetings_colleague_search()}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          )}

          {shown.length > 0 ? (
            <div className='flex max-h-90 flex-col gap-2 overflow-y-auto'>
              {shown.map((colleague) => (
                <ContactOption
                  key={colleague.id}
                  name={colleague.label}
                  email={colleague.email}
                  imageUrl={colleague.profile_image_url}
                  checked={picked === colleague.id}
                  onCheckedChange={() => pick(colleague)}
                />
              ))}
            </div>
          ) : (
            <p className='text-sm text-ink-muted'>
              {pickable.length === 0
                ? m.meetings_colleague_none_to_add()
                : m.meetings_colleague_no_match()}
            </p>
          )}

          {add.isError && (
            <p role='alert' className='text-sm text-danger'>
              {m.meetings_colleague_failed()}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={close}>
            {m.meetings_exception_cancel()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Everybody who can still be picked, the people they meet most first.
 *
 * Somebody known by name alone has no address to hang a correction on, so they
 * are left out rather than offered as a pick that cannot be saved.
 */
function addable(
  colleagues: ColleagueListItem[] | undefined,
  corrections: readonly MeetingFollowExceptionItem[],
): AddressedColleague[] {
  return (colleagues ?? [])
    .filter((one): one is AddressedColleague => one.email !== null)
    .filter((one) => followCorrectionFor(one, corrections) === undefined)
    .sort((left, right) => right.meeting_count - left.meeting_count);
}

/* Matched on the name and on every address: either is what somebody types. */
function matching(
  colleagues: readonly AddressedColleague[],
  search: string,
): readonly AddressedColleague[] {
  const needle = search.trim().toLowerCase();
  if (needle === '') return colleagues;

  return colleagues.filter(
    (one) =>
      one.label.toLowerCase().includes(needle) ||
      one.emails.some((email) => email.toLowerCase().includes(needle)),
  );
}
